import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type StaffUser } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconShield } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { getAdminProfile } from '@/lib/adminAuth';
import { describeAccess, isCritical, listSentence, permissionMeta, roleBlurb } from '@/lib/permissionCatalog';

const PAGE = 25;

/** A role is now any DEFINED role name — the four system roles or any custom
 *  role authored on the Roles page (admin-svc migration 041). The <select>
 *  options are fetched from the API rather than hardcoded. */
type Role = string;

const ROLE_TONE: Record<string, BadgeTone> = {
  superadmin: 'info',
  ops: 'ok',
  finance: 'warn',
  support: 'neutral',
};

/** Staff — manage admin_users: list with role + status, create new staff, and
 *  per-staffer edit-role / enable-disable / reset-password actions. superadmin-
 *  only (the nav entry + admin-svc both gate on staff.manage). Replaces the
 *  Filament staff/user management with no DB access required. */
export function Staff() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'staff', q, offset],
    queryFn: ({ signal }) => adminApi.staff({ q, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<StaffUser>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-slate-100">{r.name || '—'}</span> },
    { key: 'email', header: 'Email', cell: (r) => <span className="text-slate-300">{r.email}</span> },
    { key: 'role', header: 'Role', cell: (r) => <Badge tone={ROLE_TONE[r.role] ?? 'neutral'}>{r.role}</Badge> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.status === 'active' ? 'ok' : 'danger'} dot>
          {r.status}
        </Badge>
      ),
    },
    { key: 'created', header: 'Added', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'manage',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r.id)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          Manage
        </button>
      ),
    },
  ];

  const selectedStaff = selected !== null ? (data?.items ?? []).find((u) => u.id === selected) ?? null : null;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Staff"
        subtitle="Everyone with access to this portal. Add colleagues, change what they can reach, or revoke access."
        right={
          <div className="flex items-center gap-2">
            <SearchBox
              value={q}
              onChange={(v) => {
                setQ(v);
                setOffset(0);
              }}
              placeholder="Search name or email…"
            />
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="btn-primary shrink-0"
            >
              Add staff
            </button>
          </div>
        }
      />

      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading staff…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load staff" description={(error as Error)?.message} />
        ) : (
          <>
            <DataTable<StaffUser>
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(r) => String(r.id)}
              stickyHeader
              empty={
                <EmptyState
                  title="No staff found"
                  description="Nobody matches that search."
                  icon={<IconShield className="h-6 w-6" />}
                />
              }
            />
            <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
          </>
        )}
      </Card>

      {creating ? <CreateStaffDrawer onClose={() => setCreating(false)} /> : null}
      {selectedStaff ? <ManageStaffDrawer staff={selectedStaff} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** A labelled role <select>, shared by the create + edit forms. Options are the
 *  defined roles (system + custom) fetched from admin-svc.
 *
 *  Below the picker we show what the selected role actually GRANTS — its
 *  purpose, an access summary, and a named list of any sensitive permissions.
 *  Assigning a role is the moment someone gets power in this system, and the
 *  role name alone ("ops") doesn't tell you whether that includes refunding
 *  riders or banning drivers. */
function RoleSelect({ value, onChange }: { value: Role; onChange: (r: Role) => void }) {
  const { data } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: ({ signal }) => adminApi.roles(signal),
  });
  const { data: permData } = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: ({ signal }) => adminApi.permissions(signal),
  });
  const { data: detail } = useQuery({
    queryKey: ['admin', 'role', value],
    queryFn: ({ signal }) => adminApi.role(value, signal),
    enabled: !!value,
  });

  const roles = data?.items ?? [];
  const summary = roles.find((r) => r.name === value);
  const blurb = summary ? roleBlurb(summary.name, summary.description, summary.is_system) : '';
  const perms = detail?.role?.permissions ?? [];
  const catalogSize = permData?.permissions.length ?? 0;
  const criticals = perms.filter(isCritical).map((p) => permissionMeta(p).label.toLowerCase());

  return (
    <div>
      <label htmlFor="staff-role" className="block text-[12px] font-medium text-slate-300">
        Role
      </label>
      <select
        id="staff-role"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 input"
      >
        {/* Keep the current value selectable even if the list hasn't loaded. */}
        {roles.length === 0 ? <option value={value}>{value}</option> : null}
        {roles.map((r) => (
          <option key={r.name} value={r.name}>
            {r.name}
            {r.is_system ? '' : ' (custom)'}
          </option>
        ))}
      </select>
      {blurb ? <p className="mt-1.5 text-xs text-text-secondary">{blurb}</p> : null}
      {perms.length > 0 ? (
        <p className="mt-1 text-xs text-muted">{describeAccess(perms, catalogSize)}</p>
      ) : null}
      {criticals.length > 0 ? (
        <p className="mt-1 text-xs text-warn">Can {listSentence(criticals)}.</p>
      ) : null}
    </div>
  );
}

// ─── create-staff drawer ─────────────────────────────────────

/** CreateStaffDrawer — a form to add a new admin user (email + name + role +
 *  initial password). Posts to admin-svc POST /v1/admin/staff. */
function CreateStaffDrawer({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('support');
  const [password, setPassword] = useState('');
  const [ok, setOk] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => adminApi.createStaff({ email: email.trim(), name: name.trim(), role, password }),
    onSuccess: (res) => {
      setOk(`Created ${res.staff.email} (${res.staff.role}). They can sign in now.`);
      setEmail('');
      setName('');
      setPassword('');
      setRole('support');
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
    },
  });

  const validEmail = /.+@.+\..+/.test(email.trim());
  const validPassword = password.length >= 8;
  const canSubmit = validEmail && validPassword && !mutation.isPending;

  return (
    <Drawer title="Add staff" subtitle="They can sign in as soon as you create them." onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="block text-[12px] font-medium text-slate-300">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@telride.com.au"
            className="mt-1 input"
          />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-slate-300">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            className="mt-1 input"
          />
        </div>
        <RoleSelect value={role} onChange={setRole} />
        <div>
          <label className="block text-[12px] font-medium text-slate-300">Temporary password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            className="mt-1 input"
          />
          {password !== '' && !validPassword ? (
            <p className="mt-1 text-xs text-warn">Use at least 8 characters.</p>
          ) : null}
        </div>

        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            setOk(null);
            mutation.mutate();
          }}
          className="btn-primary w-full"
        >
          {mutation.isPending ? 'Creating…' : 'Create staff'}
        </button>
        {ok ? <p className="text-xs text-ok">{ok}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

// ─── manage-staff drawer (role / status / password) ──────────

/** ManageStaffDrawer — edit one staffer: change role, enable/disable, reset
 *  password. Self-targeting demote/disable is blocked client-side too (the
 *  server enforces it regardless) so a superadmin can't lock themselves out. */
function ManageStaffDrawer({ staff, onClose }: { staff: StaffUser; onClose: () => void }) {
  const qc = useQueryClient();
  const me = getAdminProfile();
  const isSelf = me?.id === staff.id;

  const [role, setRole] = useState<Role>(staff.role as Role);
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'staff'] });

  const roleMutation = useMutation({
    mutationFn: () => adminApi.updateStaff(staff.id, { role }),
    onSuccess: (res) => {
      setMsg(`Role set to ${res.staff.role}.`);
      invalidate();
    },
  });

  const statusMutation = useMutation({
    mutationFn: (next: 'active' | 'disabled') => adminApi.updateStaff(staff.id, { status: next }),
    onSuccess: (res) => {
      setMsg(res.staff.status === 'disabled' ? 'Access revoked.' : 'Access restored.');
      invalidate();
    },
  });

  const passwordMutation = useMutation({
    mutationFn: () => adminApi.resetStaffPassword(staff.id, password),
    onSuccess: () => {
      setMsg('Password reset.');
      setPassword('');
    },
  });

  const roleChanged = role !== staff.role;
  // A superadmin must not demote OR disable their own account (would orphan the
  // staff.manage permission). Reset-own-password is fine.
  const demotingSelf = isSelf && role !== 'superadmin' && staff.role === 'superadmin';
  const validPassword = password.length >= 8;

  const anyError =
    (roleMutation.error as Error) || (statusMutation.error as Error) || (passwordMutation.error as Error) || null;

  return (
    <Drawer title={staff.name || staff.email} subtitle={staff.email} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <Field label="ID" value={`#${staff.id}`} />
          <Field label="Email" value={staff.email} />
          <Field label="Current role" value={<Badge tone={ROLE_TONE[staff.role] ?? 'neutral'}>{staff.role}</Badge>} />
          <Field
            label="Status"
            value={
              <Badge tone={staff.status === 'active' ? 'ok' : 'danger'} dot>
                {staff.status}
              </Badge>
            }
          />
          <Field label="Added" value={dateTime(staff.created_at)} />
        </div>

        {/* Change role */}
        <Card>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Role</div>
          <RoleSelect value={role} onChange={setRole} />
          {demotingSelf ? (
            <p className="mt-2 text-xs text-warn">You can't remove your own superadmin access.</p>
          ) : null}
          <button
            type="button"
            disabled={!roleChanged || demotingSelf || roleMutation.isPending}
            onClick={() => {
              setMsg(null);
              roleMutation.mutate();
            }}
            className="btn-primary mt-3"
          >
            {roleMutation.isPending ? 'Saving…' : 'Save role'}
          </button>
        </Card>

        {/* Enable / disable */}
        <Card>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Access</div>
          {staff.status === 'active' ? (
            <>
              <p className="mb-3 text-xs text-muted">
                Access ends immediately. They can't sign in, and any session they have open is rejected on its next
                request. You can re-enable them at any time.
              </p>
              {confirmingDisable ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] text-slate-200">Revoke access for {staff.name || staff.email}?</span>
                  <button
                    type="button"
                    disabled={statusMutation.isPending}
                    onClick={() => {
                      setMsg(null);
                      setConfirmingDisable(false);
                      statusMutation.mutate('disabled');
                    }}
                    className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm font-semibold text-danger hover:bg-danger/20 disabled:opacity-50"
                  >
                    {statusMutation.isPending ? 'Disabling…' : 'Yes, disable'}
                  </button>
                  <button type="button" onClick={() => setConfirmingDisable(false)} className="btn-secondary">
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={isSelf || statusMutation.isPending}
                  onClick={() => setConfirmingDisable(true)}
                  className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm font-semibold text-danger hover:bg-danger/20 disabled:opacity-50"
                >
                  Disable access
                </button>
              )}
              {isSelf ? <p className="mt-2 text-xs text-warn">You can't disable your own account.</p> : null}
            </>
          ) : (
            <>
              <p className="mb-3 text-xs text-muted">This account is disabled and can’t sign in.</p>
              <button
                type="button"
                disabled={statusMutation.isPending}
                onClick={() => {
                  setMsg(null);
                  statusMutation.mutate('active');
                }}
                className="btn-primary"
              >
                {statusMutation.isPending ? 'Enabling…' : 'Restore access'}
              </button>
            </>
          )}
        </Card>

        {/* Reset password */}
        <Card>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Password</div>
          <p className="mb-3 text-xs text-muted">
            Share the new password over a secure channel and ask them to change it after signing in.
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password, at least 8 characters"
            autoComplete="new-password"
            className="input"
          />
          <button
            type="button"
            disabled={!validPassword || passwordMutation.isPending}
            onClick={() => {
              setMsg(null);
              passwordMutation.mutate();
            }}
            className="btn-primary mt-3"
          >
            {passwordMutation.isPending ? 'Resetting…' : 'Reset password'}
          </button>
        </Card>

        {msg ? <p className="text-xs text-ok">{msg}</p> : null}
        {anyError ? <p className="text-xs text-danger">{anyError.message}</p> : null}
      </div>
    </Drawer>
  );
}
